import { makeStyles, styled, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import * as DomPurify from "dompurify";
import { marked } from "marked";
import * as React from "react";
import FocusManager from "../core/FocusManager";
import Api, { ValueByBreakpoint } from "../mustangui/Api";

export type Justification = "Centered" | "Full" | "Left";

interface Props {
  autoFocus?: true;
  justification: ValueByBreakpoint<Justification>;
  value: string;
}

interface MarkdownContainerProps {
  justification: ValueByBreakpoint<Justification>;
}

marked.use({
  breaks: false,
  gfm: false,
  hooks: {
    postprocess: (html: string) => {
      return DomPurify.sanitize(html, {
        ALLOWED_ATTR: ["target", "href", "title"],
        ALLOWED_TAGS: [
          "a",
          "blockquote",
          "em",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "hr",
          "li",
          "ol",
          "p",
          "strong",
          "ul",
        ],
      });
    },
  },
  renderer: {
    code(code: string, infostring: string, escaped: boolean) {
      let infostringMarkdown = "";
      if (!!infostring) {
        infostringMarkdown = `${infostring} `;
      }
      return `<p>\`\`\`${infostringMarkdown}${code}\`\`\`</p>`;
    },
    codespan(code: string) {
      return `\`${code}\``;
    },
    html(html: string) {
      // No HTML tags are interpreted in the flavour of Markdown that we
      // support.
      return html.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    },
    image(href: string, title: string, text: string) {
      let titleMarkdown = "";
      if (title) {
        titleMarkdown = ` "${title}"`;
      }
      return `![${text}](${href}${titleMarkdown})`;
    },
    link(href: string, title: string, text: string) {
      // Helper function "borrowed" from marked.js
      // https://github.com/markedjs/marked/blob/v14.1.0/src/helpers.ts
      function cleanUrl(url: string): string | null {
        try {
          return encodeURI(url).replace(/%25/g, "%");
        } catch {
          return null;
        }
      }

      if (title === "POSSE_AUTO_LINK") {
        return text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
      }

      // Implementation based off the default Link renderer
      // https://github.com/markedjs/marked/blob/v4.3.0/src/Renderer.ts
      const cleanHref = cleanUrl(href);
      if (cleanHref === null) {
        return text;
      }

      const titleAttr = title ? ` title="${title}"` : "";
      return `<a href="${cleanHref}" target="_blank"${titleAttr}>${text}</a>`;
    },
  },
  tokenizer: {
    link(src: string) {
      // Disable auto-links
      // https://www.markdownguide.org/basic-syntax/#urls-and-email-addresses
      if (src.startsWith("<") || src.endsWith(">")) {
        // The renderer doesn't have enough information to detect an auto-link,
        // so instead it is detected here and signals the renderer with a
        // special title.
        return {
          href: src.substring(1, src.length - 1),
          raw: src,
          text: src,
          title: "POSSE_AUTO_LINK",
          tokens: [
            {
              raw: src,
              text: src,
              type: "text",
            },
          ],
          type: "link",
        };
      }

      return false;
    },
  },
});

const MarkdownContainer = styled("div")(({ theme }) => ({
  "& a": {
    color: Api.getSystemColor("information"),
    fontWeight: 700,
  },
  "& blockquote": {
    "&>:first-child": {
      marginTop: 0,
    },
    "&>:last-child": {
      marginBottom: 0,
    },
    backgroundColor: theme.palette.grey[100],
    borderLeft: `8px solid ${theme.palette.grey[800]}`,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.25)",
    fontWeight: 300,
    margin: "24px 0",
    padding: "12px 24px",
  },
  "& h1": {
    fontSize: "34px",
    lineHeight: "44px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "45px",
      lineHeight: "60px",
      marginTop: "60px",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "56px",
      lineHeight: "72px",
      marginTop: "80px",
    },
  },
  "& h1,h2,h3,h4,h5,h6": {
    fontWeight: 700,
    margin: "40px 0 24px 0",
  },
  "& h2": {
    fontSize: "24px",
    lineHeight: "32px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "34px",
      lineHeight: "44px",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "45px",
      lineHeight: "60px",
      marginTop: "60px",
    },
  },
  "& h3": {
    fontSize: "21px",
    lineHeight: "28px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "24px",
      lineHeight: "32px",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "34px",
      lineHeight: "44px",
    },
  },
  "& h4": {
    fontSize: "16px",
    lineHeight: "20px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "21px",
      lineHeight: "28px",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "24px",
      lineHeight: "32px",
    },
  },
  "& h5": {
    fontSize: "16px",
    lineHeight: "20px",
    [theme.breakpoints.up("lg")]: {
      fontSize: "21px",
      lineHeight: "28px",
    },
  },
  "& h6": {
    fontSize: "16px",
    lineHeight: "20px",
  },
  "& hr": {
    borderBottomWidth: 1,
    borderColor: theme.palette.divider,
    borderStyle: "solid",
    borderWidth: 0,
    margin: "24px 0",
  },
  "& li": {
    margin: "24px 0",
  },
  "& li li": {
    margin: "8px 0",
  },
  "& li>p": {
    margin: "8px 0",
  },
  "& ol": {
    paddingInlineStart: "40px",
  },
  "& p": {
    fontSize: "16px",
    lineHeight: "20px",
    margin: "24px 0",
  },
  "& strong": {
    fontWeight: 700,
  },
  "& ul": {
    paddingInlineStart: "40px",
  },
  "&:focus": {
    outline: "none",
  },
  "&>:first-child": {
    marginTop: 0,
  },
  "&>:last-child": {
    marginBottom: 0,
  },
  color: theme.palette.text.primary,
  fontFamily: "Roboto, sans-serif",
  fontWeight: 400,
}));

const useStyles = makeStyles(() => ({
  centeredJustify: {
    "& h1,h2,h3,h4,h5,h6,p": {
      textAlign: "center",
    },
    "& ol,ul": {
      textAlign: "start",
    },
    "& ol,ul p": {
      textAlign: "start",
    },
  },
  fullJustify: {
    "& h1,h2,h3,h4,h5,h6,p": {
      textAlign: "justify",
    },
    "& ol,ul": {
      textAlign: "justify",
    },
  },
  leftJustify: {
    "& h1,h2,h3,h4,h5,h6,p": {
      textAlign: "start",
    },
    "& ol,ul": {
      textAlign: "start",
    },
  },
}));

export default function Markdown(props: Props): JSX.Element {
  const theme = useTheme();
  const classes = useStyles();
  const containerRef = React.useRef<HTMLDivElement>(null);

  let justification: Justification = "Left";
  if (useMediaQuery(theme.breakpoints.only("xs"))) {
    justification = props.justification.xs;
  }
  if (useMediaQuery(theme.breakpoints.only("sm"))) {
    justification = props.justification.sm;
  }
  if (useMediaQuery(theme.breakpoints.only("md"))) {
    justification = props.justification.md;
  }
  if (useMediaQuery(theme.breakpoints.only("lg"))) {
    justification = props.justification.lg;
  }
  if (useMediaQuery(theme.breakpoints.only("xl"))) {
    justification = props.justification.xl;
  }

  const cleanHtml = React.useMemo(
    () => marked.parse(props.value) as string,
    [props.value]
  );

  React.useEffect(() => {
    if (props.autoFocus && containerRef.current) {
      FocusManager.grabFocus(containerRef.current);
    }
  }, [props.autoFocus, containerRef]);

  const className = {
    Centered: classes.centeredJustify,
    Full: classes.fullJustify,
    Left: classes.leftJustify,
  }[justification];

  return (
    <MarkdownContainer
      className={className}
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
      ref={containerRef}
      tabIndex={props.autoFocus ? -1 : undefined}
    />
  );
}
